import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

export type DrawerProps = {
  open: boolean
  onClose: () => void
  title?: string
  closeLabel?: string
  side?: 'left' | 'right'
  children: React.ReactNode
}

const Drawer: React.FC<DrawerProps> = ({ open, onClose, title, closeLabel = 'Close', side = 'right', children }) => {
  const panelRef = useRef<HTMLDivElement | null>(null)
  const previousActive = useRef<Element | null>(null)

  useEffect(() => {
    if (open) {
      previousActive.current = document.activeElement
      panelRef.current?.focus()
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose()
      }
      document.addEventListener('keydown', onKey)
      document.body.style.overflow = 'hidden'
      return () => {
        document.removeEventListener('keydown', onKey)
        document.body.style.overflow = ''
        if (previousActive.current instanceof HTMLElement) previousActive.current.focus()
      }
    }
  }, [open, onClose])

  if (!open) return null

  return createPortal(
    <div className="fixed inset-0 z-50" aria-modal="true" role="dialog">
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        tabIndex={-1}
        className={`absolute top-0 ${side === 'right' ? 'right-0' : 'left-0'} h-full w-80 max-w-[85vw] bg-white shadow-xl outline-none transition-transform duration-300 ease-out ${side === 'right' ? 'translate-x-0' : 'translate-x-0'}`}
      >
        <div className="border-b border-neutral-200 px-4 py-3 flex items-center justify-between">
          <h2 className="text-sm font-medium">{title}</h2>
          <button aria-label={closeLabel} onClick={onClose} className="p-2 rounded hover:bg-neutral-100">
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
              <path d="M5 5l14 14M19 5L5 19" stroke="currentColor" strokeWidth="2" />
            </svg>
          </button>
        </div>
        <div className="p-4 overflow-y-auto h-[calc(100%-3rem)]">
          {children}
        </div>
      </div>
    </div>,
    document.body
  )
}

export default Drawer
